/*
 * Convert Image to Chart — deck writeback.
 *
 * The extracted `EditableChart` becomes a NATIVE PowerPoint chart, not a
 * picture of one: PptxGenJS emits a real `c:chart` part with its embedded
 * worksheet, so the inserted chart opens in PowerPoint's own chart editor with
 * the recovered numbers in it. That is the entire point of the feature — a
 * rendered image would be no better than the picture the user already had.
 *
 * The chart is built into a one-slide .pptx and inserted right after the
 * active slide with `insertSlidesFromBase64`, the same recipe Convert to
 * Infographic and MapPlot use (see infographicInsert.ts) and the only route
 * available: the PowerPoint JS API has no `addChart`, unlike Excel's.
 *
 * The slide is sized to the live deck so the destination presentation never
 * has to reconcile a differently-sized inserted slide.
 */

import { SLIDE_HEIGHT, SLIDE_WIDTH } from "../../../const";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { devTrace } from "../../shared/services/devTrace";
import {
  CONVERT_CHART_TRACE_SCOPE,
  axisNumberFormat,
  logConvertChartError,
  shouldShowDataLabels,
  shouldShowLegend,
  type ChartSeries,
  type EditableChart,
  type NumberFormatAffix,
} from "./convertChartApi";
import { seriesColor } from "./chartPreviewModel";
import type { ChartRenderKind, ChartTypeOption } from "./chartTypeOptions";

const EMU_PER_INCH = 914400;
const POINTS_PER_INCH = 72;
const CUSTOM_LAYOUT_NAME = "AIVA_CHART_SLIDE";

/** Chart frame as a fraction of the slide, leaving room for the deck's own
 *  title band. Fractions rather than inches so a 4:3 deck gets the same
 *  proportions as a 16:9 one. */
const CHART_FRAME = { x: 0.06, y: 0.14, w: 0.88, h: 0.76 } as const;

export const CHART_INSERT_UNAVAILABLE_MESSAGE =
  "PowerPoint API unavailable — the chart couldn't be added to the deck.";
export const CHART_INSERT_UNSUPPORTED_MESSAGE =
  "This PowerPoint version can't insert slides from base64 (needs PowerPointApi 1.2+).";
export const CHART_INSERT_FAILED_MESSAGE = "Adding the chart to the deck failed. Nothing was changed — try again.";

/* ── EditableChart → PptxGenJS ─────────────────────────────────────────── */

/** PptxGenJS chart names. It has no "column": a column chart is `bar` with
 *  `barDir: "col"`, which is also how OOXML itself models it. */
type PptxChartName = "area" | "bar" | "doughnut" | "line" | "pie" | "radar" | "scatter";

/** Which way a bar chart's bars run — PptxGenJS's `barDir`. Named because the
 *  same union appears on the options, the tile mapping and each combo group. */
type PptxBarDir = "col" | "bar";

/** PptxGenJS's one-letter legend placements — bottom, left, right, top. */
type PptxLegendPos = "b" | "l" | "r" | "t";

interface PptxChartRow {
  readonly name: string;
  readonly labels: string[];
  readonly values: Array<number | null>;
}

/** The PptxGenJS option keys this builder sets. Declared locally rather than
 *  imported: the library's `IChartOpts` is a ~200-key surface, and naming the
 *  handful actually used keeps a typo a compile error instead of a silently
 *  ignored option. */
interface PptxChartOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  barDir?: PptxBarDir;
  barGrouping?: "clustered" | "stacked" | "percentStacked";
  chartColors?: string[];
  showLegend?: boolean;
  legendPos?: PptxLegendPos;
  showValue?: boolean;
  showTitle?: boolean;
  title?: string;
  catAxisTitle?: string;
  showCatAxisTitle?: boolean;
  valAxisTitle?: string;
  showValAxisTitle?: boolean;
  valAxisMinVal?: number;
  valAxisMaxVal?: number;
  valAxisMajorUnit?: number;
  holeSize?: number;
  lineSmooth?: boolean;
  valAxisLabelFormatCode?: string;
  dataLabelFormatCode?: string;
  secondaryValAxis?: boolean;
  secondaryCatAxis?: boolean;
  /** Per-axis overrides. PptxGenJS emits a SECOND axis pair only when both of
   *  these are present with two entries — see {@link dualAxisOptions}. */
  catAxes?: PptxCatAxisOptions[];
  valAxes?: PptxValAxisOptions[];
}

/** The subset of a category axis this builder overrides per axis. */
interface PptxCatAxisOptions {
  catAxisTitle?: string;
  showCatAxisTitle?: boolean;
  catAxisHidden?: boolean;
}

/** The subset of a value axis this builder overrides per axis. */
interface PptxValAxisOptions {
  valAxisTitle?: string;
  showValAxisTitle?: boolean;
  valAxisHidden?: boolean;
  valAxisLabelFormatCode?: string;
  valAxisMinVal?: number;
  valAxisMaxVal?: number;
  valAxisMajorUnit?: number;
}

interface PptxChartPart {
  readonly type: PptxChartName;
  readonly data: PptxChartRow[];
  readonly options: PptxChartOptions;
}

/**
 * How `addChart` must be called — a discriminated union, because PptxGenJS
 * takes its arguments differently for the two cases and getting it wrong fails
 * deep inside XML generation rather than at the call.
 *
 * For a single type it is `addChart(type, data, options)`. For a multi-type
 * (combo) chart it is `addChart(parts, options)` — TWO arguments: the library
 * does `tmpOpt = data || opt` when `type` is an array (pptxgen.es.js ~L1690),
 * so the SECOND argument is the options object there. Its own `.d.ts` declares
 * the three-argument form for both, so the type system does not catch this.
 *
 * Passing a data array as that second argument is not merely ignored, it is
 * actively harmful: the library then reads deprecated top-level options off it,
 * and `if (options.fill)` picks up `Array.prototype.fill` — a function — as the
 * plot-area colour, which surfaces later as
 * "(colorStr || '').replace is not a function".
 */
type PptxChartSpec =
  | { readonly kind: "single"; readonly type: PptxChartName; readonly data: PptxChartRow[]; readonly options: PptxChartOptions }
  | { readonly kind: "multi"; readonly parts: readonly PptxChartPart[]; readonly options: PptxChartOptions };

/** Tile → the PptxGenJS chart name and bar direction it rebuilds as. */
const PPTX_CHART_BY_RENDER: Readonly<Record<ChartRenderKind, { name: PptxChartName; barDir?: PptxBarDir }>> = {
  column: { name: "bar", barDir: "col" },
  bar: { name: "bar", barDir: "bar" },
  line: { name: "line" },
  area: { name: "area" },
  pie: { name: "pie" },
  doughnut: { name: "doughnut" },
  scatter: { name: "scatter" },
  radar: { name: "radar" },
  // A combo is emitted as an IChartMulti list; this is only its base frame.
  combo: { name: "bar", barDir: "col" },
};

const LEGEND_POS_BY_POSITION: Readonly<Record<string, PptxLegendPos>> = {
  bottom: "b",
  top: "t",
  left: "l",
  right: "r",
};

/** `legend_position` → PptxGenJS's one-letter code, defaulting to the bottom
 *  the extraction's own default implies. Guarded with a `typeof` check because
 *  the key crosses an API boundary (§3 of the coding standards). */
function legendPos(position: string | null): PptxLegendPos {
  if (!position) return "b";
  const code = LEGEND_POS_BY_POSITION[position];
  return typeof code === "string" ? code : "b";
}

/**
 * One PptxGenJS data row per series.
 *
 * `null` is passed through rather than coerced to 0: PptxGenJS writes it as an
 * empty worksheet cell, which PowerPoint renders as a gap. A zero would invent
 * a data point the model explicitly said it could not read.
 */
/**
 * An affix → an Excel number format code.
 *
 * Deliberately NOT Excel's own `0%`: that multiplies by 100, and the extraction
 * already reports percentages in display units (42 for "42%"), so `0%` would
 * render 4200%. A quoted literal shows the number as read and appends the
 * symbol, which is what the source axis did.
 */
export function numberFormatCode(affix: NumberFormatAffix): string {
  const prefix = affix.prefix ? `"${affix.prefix}"` : "";
  const suffix = affix.suffix ? `"${affix.suffix}"` : "";
  return `${prefix}#,##0.###${suffix}`;
}

function chartRows(chart: EditableChart, series: readonly ChartSeries[]): PptxChartRow[] {
  const labels = [...chart.categories];
  return series.map((entry, index) => ({
    name: entry.name || `Series ${index + 1}`,
    labels,
    values: [...entry.values],
  }));
}

/**
 * PptxGenJS's name for the X-axis row of a scatter chart. The library reads
 * `data[0]` as the X values and plots `data[1..]` against them
 * (`data.filter((_obj, idx) => idx > 0)` in its scatter emitter), so this row
 * is structural — it is not a series and never appears in the legend.
 */
const SCATTER_X_AXIS_ROW_NAME = "X-Axis";

/**
 * A scatter chart's rows: the X values first, then one row per series.
 *
 * `categories` carries the numeric x-values as strings on a scatter chart —
 * ChartType.scatter's own docstring says so — and they have to be handed over
 * as `data[0]`. Omitting them does not merely lose the x-axis: PptxGenJS takes
 * whatever is in `data[0]` as the X values, so the FIRST EXTRACTED SERIES gets
 * eaten, plotting the chart against its own y-readings and dropping it from the
 * legend.
 *
 * A category that won't parse falls back to its ordinal position, matching the
 * preview's scatterShapes so the two draw the same points.
 */
function scatterRows(chart: EditableChart): PptxChartRow[] {
  const labels = [...chart.categories];
  const xValues = chart.categories.map((category, index) => {
    const parsed = Number(category);
    return Number.isFinite(parsed) ? parsed : index;
  });
  return [{ name: SCATTER_X_AXIS_ROW_NAME, labels, values: xValues }, ...chartRows(chart, chart.series)];
}

/** Series colours in PptxGenJS's `chartColors` slot order. Pie/doughnut colour
 *  per *category*, every other type per *series* — the same split the API uses
 *  between `slice_colors` and `ChartSeries.color`. */
function chartColors(chart: EditableChart, render: ChartRenderKind): string[] {
  if (render === "pie" || render === "doughnut") {
    return chart.categories.map((_, index) => chart.sliceColors?.[index] ?? seriesColor(index, null));
  }
  return chart.series.map((series, index) => seriesColor(index, series.color));
}

function baseOptions(chart: EditableChart, option: ChartTypeOption, frame: PptxChartOptions): PptxChartOptions {
  const mapping = PPTX_CHART_BY_RENDER[option.render];
  const options: PptxChartOptions = {
    ...frame,
    ...(mapping.barDir ? { barDir: mapping.barDir } : {}),
    chartColors: chartColors(chart, option.render),
    showLegend: shouldShowLegend(chart),
    legendPos: legendPos(chart.legendPosition),
    showValue: shouldShowDataLabels(chart),
  };

  if (option.stacked) options.barGrouping = "stacked";
  if (chart.title) {
    options.title = chart.title;
    options.showTitle = true;
  }
  if (chart.xAxisTitle) {
    options.catAxisTitle = chart.xAxisTitle;
    options.showCatAxisTitle = true;
  }
  if (chart.yAxisTitle) {
    options.valAxisTitle = chart.yAxisTitle;
    options.showValAxisTitle = true;
  }
  // Reproduce the source chart's own scale when the model read it off the tick
  // labels; otherwise let PowerPoint auto-scale, which is its default.
  if (typeof chart.yAxis?.min === "number") options.valAxisMinVal = chart.yAxis.min;
  if (typeof chart.yAxis?.max === "number") options.valAxisMaxVal = chart.yAxis.max;
  if (typeof chart.yAxis?.majorUnit === "number" && chart.yAxis.majorUnit > 0) {
    options.valAxisMajorUnit = chart.yAxis.majorUnit;
  }
  // Put the source's currency/percent symbol back on the ticks — and on the
  // data labels, so the two never disagree. `axisNumberFormat` returns null
  // when the chart has two value axes, because the API reports one format for
  // the whole chart and labelling the wrong axis would misstate the data.
  const affix = axisNumberFormat(chart);
  if (affix) {
    const code = numberFormatCode(affix);
    options.valAxisLabelFormatCode = code;
    if (shouldShowDataLabels(chart)) options.dataLabelFormatCode = code;
  }
  if (option.render === "doughnut") options.holeSize = 50;
  if (option.render === "line" && chart.series.some((series) => series.lineSmooth)) options.lineSmooth = true;

  return options;
}

/**
 * The `catAxes`/`valAxes` pair a dual-axis combo needs on its OUTER options.
 *
 * Setting `secondaryValAxis` on a part only changes which `c:axId` that part's
 * series *reference*. The axes themselves are emitted from the outer options,
 * and only when both arrays are present with two entries
 * (pptxgen.es.js ~L3460-3485). Without them the secondary series point at an
 * axId that was never written, and PowerPoint rejects the whole file —
 * `insertSlidesFromBase64` fails with a bare "InvalidArgument".
 *
 * Two constraints the library enforces by throwing, both satisfied here:
 * the arrays must be the same length, and if there are two value axes at least
 * one part must actually use the secondary one (this is only called when one
 * does).
 *
 * The secondary *category* axis is hidden: it plots the same categories as the
 * primary, so drawing it twice just doubles the labels under the chart, which
 * is also what PowerPoint does for a dual-axis combo built by hand.
 */
function axisScale(axis: EditableChart["yAxis"]): PptxValAxisOptions {
  if (!axis) return {};
  return {
    ...(typeof axis.min === "number" ? { valAxisMinVal: axis.min } : {}),
    ...(typeof axis.max === "number" ? { valAxisMaxVal: axis.max } : {}),
    ...(typeof axis.majorUnit === "number" && axis.majorUnit > 0 ? { valAxisMajorUnit: axis.majorUnit } : {}),
  };
}

function dualAxisOptions(chart: EditableChart, options: PptxChartOptions): PptxChartOptions {
  // Every per-axis key is REMOVED from the outer options and redistributed
  // below. PptxGenJS renders each axis from {...outerOptions, ...valAxes[n]},
  // so anything axis-shaped left on the outer object silently becomes the
  // default for BOTH axes — which is how a 0-1.8 "$ billion" scale ended up on
  // a right-hand axis holding percentages, flattening the line series into the
  // floor of the plot.
  const {
    valAxisTitle,
    showValAxisTitle,
    valAxisMinVal,
    valAxisMaxVal,
    valAxisMajorUnit,
    valAxisLabelFormatCode,
    catAxisTitle,
    // Destructured only to strip it from the outer options; the per-axis entry
    // below sets its own.
    showCatAxisTitle: _showCatAxisTitle,
    ...chartLevel
  } = options;

  return {
    ...chartLevel,
    catAxes: [
      { ...(catAxisTitle ? { catAxisTitle, showCatAxisTitle: true } : {}) },
      // The secondary category axis repeats the primary's labels, so drawing it
      // would just double the labels under the chart.
      { catAxisHidden: true },
    ],
    valAxes: [
      {
        ...(valAxisTitle ? { valAxisTitle, showValAxisTitle } : {}),
        ...(typeof valAxisMinVal === "number" ? { valAxisMinVal } : {}),
        ...(typeof valAxisMaxVal === "number" ? { valAxisMaxVal } : {}),
        ...(typeof valAxisMajorUnit === "number" ? { valAxisMajorUnit } : {}),
        ...(valAxisLabelFormatCode ? { valAxisLabelFormatCode } : {}),
      },
      {
        ...(chart.secondaryYAxisTitle
          ? { valAxisTitle: chart.secondaryYAxisTitle, showValAxisTitle: true }
          : { valAxisTitle: "", showValAxisTitle: false }),
        // The secondary series are on their own scale — percentages against a
        // currency axis, typically. Use the scale the model read off the source
        // image's right-hand axis; with none, leave it for PowerPoint to fit.
        ...axisScale(chart.secondaryYAxis),
      },
    ],
  };
}

/**
 * A combo chart as PptxGenJS's `IChartMulti` list: one entry per render kind
 * present, each carrying only the series drawn that way.
 *
 * Series on the `secondary` axis are split into their own entry with
 * `secondaryValAxis`, which is how a dual-axis combo (the case the API's
 * `axis` field exists for) round-trips into a real PowerPoint chart.
 */
function comboSpec(chart: EditableChart, options: PptxChartOptions): PptxChartSpec {
  interface ComboGroup {
    readonly name: PptxChartName;
    readonly barDir?: PptxBarDir;
    readonly secondary: boolean;
    readonly series: ChartSeries[];
  }
  const groups = new Map<string, ComboGroup>();

  for (const series of chart.series) {
    const render: ChartRenderKind = series.renderAs ?? "column";
    const mapping = PPTX_CHART_BY_RENDER[render] ?? PPTX_CHART_BY_RENDER.column;
    const secondary = series.axis === "secondary";
    const key = `${render}:${secondary ? "secondary" : "primary"}`;
    const existing = groups.get(key);
    if (existing) existing.series.push(series);
    else groups.set(key, { name: mapping.name, ...(mapping.barDir ? { barDir: mapping.barDir } : {}), secondary, series: [series] });
  }

  const parts = Array.from(groups.values()).map((group) => ({
    type: group.name,
    data: chartRows(chart, group.series),
    options: {
      ...options,
      ...(group.barDir ? { barDir: group.barDir } : {}),
      // A part's options only decide which axId its series reference; the axis
      // itself comes from the outer `valAxes`/`catAxes` added below.
      ...(group.secondary ? { secondaryValAxis: true, secondaryCatAxis: true } : {}),
    },
  }));

  // A "combo" whose series all render the same way is just that one type —
  // emitting a single-entry multi list would nest a chart for no reason.
  const only = parts[0];
  if (parts.length <= 1) {
    return { kind: "single", type: only?.type ?? "bar", data: chartRows(chart, chart.series), options };
  }
  // The outer options carry everything chart-level (frame, legend, title,
  // axes); each part's own options carry only how its series are drawn. They
  // are the full option set rather than just the frame so a combo keeps the
  // legend and titles every other tile gets.
  const usesSecondary = parts.some((part) => part.options.secondaryValAxis === true);
  return {
    kind: "multi",
    parts,
    options: usesSecondary ? dualAxisOptions(chart, options) : options,
  };
}

/** The PptxGenJS (type, data, options) triple for one extraction + tile. */
export function buildChartSpec(chart: EditableChart, option: ChartTypeOption, slide: { w: number; h: number }): PptxChartSpec {
  const frame: PptxChartOptions = {
    x: slide.w * CHART_FRAME.x,
    y: slide.h * CHART_FRAME.y,
    w: slide.w * CHART_FRAME.w,
    h: slide.h * CHART_FRAME.h,
  };
  const options = baseOptions(chart, option, frame);

  if (option.render === "combo") return comboSpec(chart, options);
  return {
    kind: "single",
    type: PPTX_CHART_BY_RENDER[option.render].name,
    data: option.render === "scatter" ? scatterRows(chart) : chartRows(chart, chart.series),
    options,
  };
}

/* ── Slide build + insert ──────────────────────────────────────────────── */

/** Slide size in INCHES, which is the unit PptxGenJS lays out in. Falls back
 *  to the standard 16:9 default on any read failure. */
async function readSlideSizeInches(): Promise<{ w: number; h: number }> {
  const fallback = { w: SLIDE_WIDTH / POINTS_PER_INCH, h: SLIDE_HEIGHT / POINTS_PER_INCH };
  if (!hasPpt()) return fallback;
  try {
    return await ppt().run(async (context) => {
      const pageSetup = context.presentation.pageSetup;
      pageSetup.load("slideWidth,slideHeight");
      await context.sync();
      const widthPts = typeof pageSetup.slideWidth === "number" && pageSetup.slideWidth > 0 ? pageSetup.slideWidth : SLIDE_WIDTH;
      const heightPts =
        typeof pageSetup.slideHeight === "number" && pageSetup.slideHeight > 0 ? pageSetup.slideHeight : SLIDE_HEIGHT;
      return { w: widthPts / POINTS_PER_INCH, h: heightPts / POINTS_PER_INCH };
    });
  } catch (error) {
    logConvertChartError("convert-chart-slide-size-read", getErrorMessage(error, "pageSetup read failed"));
    return fallback;
  }
}

/** PptxGenJS's `addChart` accepts a much wider option surface than
 *  {@link PptxChartOptions} names, and its two argument forms (see
 *  {@link PptxChartSpec}) do not both fit its own declared signature. This is
 *  the one cast that bridges the two, kept in a single place so no call site
 *  reaches for `any`. */
type AddChartFn = (...args: unknown[]) => unknown;

/**
 * Build the one-slide .pptx holding the chart, as base64.
 *
 * PptxGenJS is imported dynamically rather than at module scope. This module
 * is reachable from ChatTabPanel (via the sheet), which mounts for Word and
 * Outlook too, and the library is only needed once someone actually presses
 * Create Chart — an eager import would put a megabyte of .pptx writer in the
 * chat panel's load path for every host, and measurably slows every test that
 * mounts the panel. The other pptxgenjs users (MapPlot, Infographic) sit
 * behind DeckorateModule, so they do not have the same reach.
 */
export async function buildChartSlideBase64(
  chart: EditableChart,
  option: ChartTypeOption,
  slide: { w: number; h: number },
): Promise<string> {
  const spec = buildChartSpec(chart, option, slide);

  const { default: PptxGenJS } = await import("pptxgenjs");
  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: CUSTOM_LAYOUT_NAME, width: slide.w, height: slide.h });
  pptx.layout = CUSTOM_LAYOUT_NAME;
  const target = pptx.addSlide();
  // Called as a method, never through a local alias: `addChart` reads
  // `this._slideObjects`, so detaching it strips the receiver and fails with
  // "Cannot read properties of undefined". Two arguments for a combo, three
  // otherwise — see PptxChartSpec.
  if (spec.kind === "multi") (target.addChart as AddChartFn).call(target, spec.parts, spec.options);
  else (target.addChart as AddChartFn).call(target, spec.type, spec.data, spec.options);

  return (await pptx.write({ outputType: "base64" })) as string;
}

interface InsertSlidesFromBase64Options {
  formatting: "UseDestinationTheme" | "KeepSourceFormatting";
  targetSlideId?: string;
}

type PresentationWithBase64Insert = PowerPoint.Presentation & {
  insertSlidesFromBase64?: (base64: string, options?: InsertSlidesFromBase64Options) => void;
};

/** Office/RichApi errors carry their real explanation in `.message` and
 *  `.debugInfo`, separate from the terse `.code`/`.name` that otherwise ends
 *  up as the whole of `Error.message` — surface all of it so a failure is
 *  diagnosable instead of showing a bare "InvalidArgument". */
function describeOfficeError(error: unknown): string {
  if (!error || typeof error !== "object") return String(error);
  const err = error as {
    name?: string;
    code?: string;
    message?: string;
    debugInfo?: { message?: string; errorLocation?: string };
  };
  const parts = [err.code ?? err.name, err.message, err.debugInfo?.message, err.debugInfo?.errorLocation].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  return parts.length > 0 ? Array.from(new Set(parts)).join(" — ") : CHART_INSERT_FAILED_MESSAGE;
}

/** Insert the built deck right after the active slide. */
async function insertChartSlide(base64Pptx: string): Promise<void> {
  if (!hasPpt()) throw new Error(CHART_INSERT_UNAVAILABLE_MESSAGE);

  try {
    await ppt().run(async (context) => {
      const presentation = context.presentation as PresentationWithBase64Insert;
      if (typeof presentation.insertSlidesFromBase64 !== "function") {
        throw new TypeError(CHART_INSERT_UNSUPPORTED_MESSAGE);
      }
      const selected = context.presentation.getSelectedSlides();
      selected.load("items/id");
      await context.sync();

      const targetSlideId = selected.items[0]?.id;
      presentation.insertSlidesFromBase64(base64Pptx, {
        formatting: "UseDestinationTheme",
        ...(targetSlideId ? { targetSlideId } : {}),
      });
      await context.sync();
    });
  } catch (error) {
    throw new Error(describeOfficeError(error));
  }
}

/** Rejects with the same AbortError shape `fetch` produces, so `isAbortError`
 *  and `runAbortable` classify a stopped insert exactly like a stopped request. */
function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
}

export interface ChartInsertSummary {
  /** 1-based index of the slide the chart landed on, when it could be read. */
  readonly slideIndex: number | null;
  readonly categoryCount: number;
  readonly seriesCount: number;
}

/** The 1-based index of the slide after the active one — where the insert
 *  lands. Read *before* inserting, since the selection can move afterwards. */
async function readInsertedSlideIndex(): Promise<number | null> {
  if (!hasPpt()) return null;
  try {
    return await ppt().run(async (context) => {
      const slides = context.presentation.slides;
      const selected = context.presentation.getSelectedSlides();
      slides.load("items/id");
      selected.load("items/id");
      await context.sync();
      const activeId = selected.items[0]?.id;
      if (!activeId) return null;
      const index = slides.items.findIndex((slide) => slide.id === activeId);
      return index >= 0 ? index + 2 : null;
    });
  } catch (error) {
    logConvertChartError("convert-chart-slide-index-read", getErrorMessage(error, "slide index read failed"));
    return null;
  }
}

/**
 * Build and insert the chart, returning what to tell the user.
 *
 * Nothing is written to the deck until the .pptx is fully built, so a build
 * failure leaves the presentation untouched — which is what every error
 * message in this feature promises.
 *
 * `signal` is honoured up to, and only up to, that point. A slide already
 * added cannot be un-added, so once `insertSlidesFromBase64` has been queued
 * this runs to completion and reports the insert even if Stop was pressed
 * meanwhile — reporting "cancelled" over a deck that had just changed would be
 * the one outcome worse than ignoring the click.
 */
export async function insertChartFromExtraction(
  chart: EditableChart,
  option: ChartTypeOption,
  signal?: AbortSignal,
): Promise<ChartInsertSummary> {
  const slide = await readSlideSizeInches();
  const slideIndex = await readInsertedSlideIndex();
  throwIfAborted(signal);

  devTrace(CONVERT_CHART_TRACE_SCOPE, "building chart slide", {
    render: option.render,
    categories: chart.categories.length,
    series: chart.series.length,
    slideWidthEmu: Math.round(slide.w * EMU_PER_INCH),
  });

  const base64 = await buildChartSlideBase64(chart, option, slide);
  // Last chance: building a multi-series .pptx is the slowest step, so this is
  // where a Stop pressed during "Creating chart…" most often lands.
  throwIfAborted(signal);
  await insertChartSlide(base64);

  devTrace(CONVERT_CHART_TRACE_SCOPE, "chart slide inserted", { slideIndex });
  return { slideIndex, categoryCount: chart.categories.length, seriesCount: chart.series.length };
}
